import { Button } from '@jouzen/ecom-components/v3/Button';
import { Price } from '@jouzen/ecom-components/v3/Price';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';

import { usePDPContext } from '@/app/[locale]/product/_contexts/PDPProvider';
import { useWizardStateContext } from '@/app/[locale]/product/_contexts/WizardStateProvider';
import useFooterPrice from '@/app/[locale]/product/_hooks/useFooterPrice';
import WizStepId from '@/app/[locale]/product/_steps/enums/WizStepId';
import StepCTA from '@/app/[locale]/product/_steps/StepCTA';
import AffirmPromo from '@/app/[locale]/store/_components/AffirmPromo';
import HsaFsaInlineLink from '@/app/components/HsaFsaInlineLink';
import type { RingStyleEnum } from '@/app/enums/RingStyleEnum';

import BasePrice from '../BasePrice';

const Footer = () => {
  const {
    currentStep,
    hasMaxChargerCount,
    hasMaxRingCount,
    isAccessory,
    itemCountForIcon,
    filteredFinishByInStock,
    product,
  } = useWizardStateContext();
  const { isSuccess: isProductSuccess } = usePDPContext();
  const t = useTranslations();
  const { affirmPrice, currencyCode, discount, showAffirm, totalPrice } =
    useFooterPrice();
  const style = product?.handle.split(/-(.*)/)[0];
  const isNotStyleStep = isAccessory || currentStep !== WizStepId.Style;
  const isStyleStep = currentStep === WizStepId.Style;

  return (
    <footer
      className={cx(
        'fixed inset-x-0 bottom-0 z-30 flex items-center justify-between px-4 py-2',
        'border-t border-gray-300 bg-sandstone-100',
        'lg:absolute lg:py-4',
      )}
    >
      {hasMaxChargerCount || hasMaxRingCount ? (
        <Button
          className="w-full"
          data-cy="multi-ring-max-cart"
          href="/cart"
          variant="secondary-dark"
        >
          {t('view_your_cart')}&nbsp;
          <span>{`( ${itemCountForIcon} )`}</span>
        </Button>
      ) : (
        <>
          <div
            className={cx('max-w-[45%] md:max-w-none', {
              'self-start': showAffirm,
            })}
          >
            <div
              className="text-xs font-bold lg:text-base"
              data-cy="footer-price"
            >
              {isProductSuccess && isNotStyleStep && (
                <Price
                  currencyCode={currencyCode}
                  discount={discount}
                  price={totalPrice}
                  priceCompare
                  showCurrency
                  showZeroAsFree
                />
              )}
              {isProductSuccess && isStyleStep && (
                <BasePrice
                  style={style as RingStyleEnum}
                  rings={filteredFinishByInStock}
                />
              )}
              {showAffirm && (
                <Typography className="text-xs" Element="div">
                  <AffirmPromo
                    className="text-sandstone-500"
                    pageType="product"
                    price={affirmPrice}
                  />
                </Typography>
              )}
              <HsaFsaInlineLink
                className="text-xs"
                typographyProps={{ color: 'inherit' }}
              />
            </div>
          </div>
          <StepCTA />
        </>
      )}
    </footer>
  );
};

export default Footer;
